import {
	type UseMutationOptions,
	useMutation,
	useQueryClient
} from '@tanstack/react-query'
import { httpAddNeighborhoodsToSubgeo } from '@/features/subgeo/http/add-neighborhoods-to-subgeo.http.ts'
import { httpRemoveNeighborhoodsFromSubgeo } from '@/features/subgeo/http/remove-neighborhoods-from-subgeo.http.ts'
import {
	type HttpUpdateSubgeoParams,
	httpUpdateSubgeo
} from '@/features/subgeo/http/update-subgeo.http.ts'
import { useSubgeoStore } from '@/features/subgeo/store/subgeo-store'
import type { ApiSubgeo, Subgeo } from '@/features/subgeo/types.ts'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS } from '@/query-client'

type UseUpdateSubgeoPayload = HttpUpdateSubgeoParams['payload']

export const useUpdateSubgeo = (
	urlParams: HttpUpdateSubgeoParams['urlParams'],
	context: {
		currentSubgeo: Subgeo
		selectedNeighborhoods: Set<string>
		detachedNeighborhoods: Set<string>
	},
	options?: UseMutationOptions<ApiSubgeo, unknown, UseUpdateSubgeoPayload>
) => {
	const queryClient = useQueryClient()

	return useMutation({
		...options,
		mutationFn: async (payload) => {
			const detachedNeighborhoodIds = Array.from(
				context.detachedNeighborhoods
			)
			const selectedNeighborhoodIds = Array.from(
				context.selectedNeighborhoods
			)

			if (detachedNeighborhoodIds.length > 0) {
				await httpRemoveNeighborhoodsFromSubgeo({
					urlParams,
					payload: {
						neighborhoodIds: detachedNeighborhoodIds
					},
					params: {
						responseType: 'extended'
					}
				})
			}

			if (selectedNeighborhoodIds.length > 0) {
				await httpAddNeighborhoodsToSubgeo({
					urlParams,
					payload: {
						neighborhoodIds: selectedNeighborhoodIds
					},
					params: {
						responseType: 'extended'
					}
				})
			}

			if (payload.name || payload.geoCode) {
				return await httpUpdateSubgeo({
					urlParams,
					payload,
					params: {
						responseType: 'extended'
					}
				})
			}

			return context.currentSubgeo as unknown as ApiSubgeo
		},
		onMutate: (variables) => {
			const currentSubgeos = useSubgeoStore.getState().subgeos
			const currentNeighborhoodsInSubgeo =
				context.currentSubgeo.neighborhoodIds.filter(
					(id) => !context.detachedNeighborhoods.has(id)
				)
			const newNeighborhoods = Array.from(context.selectedNeighborhoods)
			const allNeighborhoods = [
				...new Set([
					...currentNeighborhoodsInSubgeo,
					...newNeighborhoods
				])
			]

			const updatedSubgeos = currentSubgeos.map((s) => {
				if (s.id === context.currentSubgeo.id) {
					return {
						...s,
						name: variables.name || s.name,
						neighborhoodIds: allNeighborhoods
					}
				}
				return {
					...s,
					neighborhoodIds: s.neighborhoodIds.filter(
						(id) => !newNeighborhoods.includes(id)
					)
				}
			})

			useSubgeoStore.getState().setSubgeos(updatedSubgeos)

			return { previousSubgeos: currentSubgeos }
		},
		onError: (error, _variables, mutationContext) => {
			if (
				mutationContext &&
				typeof mutationContext === 'object' &&
				'previousSubgeos' in mutationContext
			) {
				useSubgeoStore
					.getState()
					.setSubgeos(mutationContext.previousSubgeos as Subgeo[])
			}
			errorHandler(error)
		},
		onSuccess: (data, variables, mutationContext, context) => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_SUBGEOS]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_NEIGHBORHOODS]
			})
			options?.onSuccess?.(data, variables, mutationContext, context)
		}
	})
}
